import React, { useEffect, useState } from 'react';
import { Plus, X, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatINR, formatDate } from '../lib/format';

export default function KitInventory() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get('/kit-items').then(r => setItems(r.data.filter(x => !x.deleted_at)));
  useEffect(() => { load(); }, []);
  const save = async (it) => { if (it.id) await api.put(`/kit-items/${it.id}`, it); else await api.post('/kit-items', it); toast.success('Saved'); setEdit(null); load(); };
  const rm = async (id) => { await api.delete(`/kit-items/${id}`); load(); };
  const today = new Date().toISOString().slice(0,10);
  const in30 = new Date(Date.now() + 30*86400000).toISOString().slice(0,10);
  const expiring = items.filter(x => x.expiry_date && x.expiry_date <= in30);
  return (
    <div className="px-5 py-5 lg:px-10 lg:py-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div><div className="eyebrow">Products</div><h1 className="serif text-[28px]">Kit Inventory</h1></div>
        <button className="btn-gold" onClick={() => setEdit({ product: '', purchase_date: today })} data-testid="kit-add"><Plus size={14}/> Add</button>
      </div>
      {expiring.length > 0 && <div className="card-alt p-4 mb-4"><div className="flex items-center gap-2 mb-1"><AlertTriangle size={14} style={{color:'var(--pending)'}}/><div className="eyebrow">Expiring within 30 days</div></div><div className="text-sm">{expiring.map(x => x.product).join(', ')}</div></div>}
      <div className="space-y-2">
        {items.length === 0 && <div className="card-cream p-8 text-center text-sm text-[var(--muted)] italic">Kit is empty.</div>}
        {items.map(k => {
          const expired = k.expiry_date && k.expiry_date < today;
          const warn = k.expiry_date && k.expiry_date <= in30;
          return (
            <div key={k.id} className="card-cream p-4 flex items-center justify-between gap-3">
              <div>
                <div className="serif text-[17px]">{k.product} <span className="text-xs text-[var(--muted)] ml-2">{k.brand}</span></div>
                <div className="text-xs text-[var(--muted)] mt-1">{k.category}{k.shade?` · ${k.shade}`:''} · bought {formatDate(k.purchase_date)}{k.expiry_date?` · expires ${formatDate(k.expiry_date)}`:''}</div>
              </div>
              <div className="text-right"><div className="mono-num">{formatINR(k.cost)}</div>
                {expired && <span className="status-pill status-overdue">expired</span>}
                {!expired && warn && <span className="status-pill status-pending">expiring</span>}
                {k.low_stock && <span className="status-pill status-pending ml-1">low stock</span>}
                <div className="flex gap-2 justify-end mt-1"><button onClick={() => setEdit(k)} className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase">Edit</button><button onClick={() => rm(k.id)} className="text-[var(--overdue)]"><Trash2 size={12}/></button></div>
              </div>
            </div>
          );
        })}
      </div>
      {edit && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/40 p-0 lg:p-6">
          <div className="w-full max-w-md card-cream p-6 space-y-3">
            <div className="flex items-center justify-between"><h2 className="serif text-[22px]">Kit Item</h2><button onClick={() => setEdit(null)}><X size={18}/></button></div>
            <div><label className="eyebrow">Product</label><input value={edit.product} onChange={e => setEdit({...edit, product: e.target.value})} data-testid="kit-product"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="eyebrow">Brand</label><input value={edit.brand||''} onChange={e => setEdit({...edit, brand: e.target.value})}/></div>
              <div><label className="eyebrow">Category</label><input value={edit.category||''} onChange={e => setEdit({...edit, category: e.target.value})}/></div>
              <div><label className="eyebrow">Shade</label><input value={edit.shade||''} onChange={e => setEdit({...edit, shade: e.target.value})}/></div>
              <div><label className="eyebrow">Cost (₹)</label><input type="number" value={edit.cost||0} onChange={e => setEdit({...edit, cost: parseFloat(e.target.value||0)})}/></div>
              <div><label className="eyebrow">Purchased</label><input type="date" value={edit.purchase_date||''} onChange={e => setEdit({...edit, purchase_date: e.target.value})}/></div>
              <div><label className="eyebrow">Expires</label><input type="date" value={edit.expiry_date||''} onChange={e => setEdit({...edit, expiry_date: e.target.value})}/></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" style={{width:'auto'}} checked={!!edit.low_stock} onChange={e => setEdit({...edit, low_stock: e.target.checked})}/> Low stock</label>
            <button className="btn-gold w-full" onClick={() => save(edit)} data-testid="kit-save">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}
